<script setup lang="ts">
import { InvoiceDetails } from '@/Pejer/types/invoice'
import { Printer } from 'lucide-vue-next'
import DataStateSkeleton from 'pejer/src/components/DataState/DataStateSkeleton.vue'
import HorizontalLine from 'pejer/src/components/HorizontalLine.vue'
import { Badge } from 'pejer/src/components/ui/badge'
import { Button } from 'pejer/src/components/ui/button'
import { Skeleton } from 'pejer/src/components/ui/skeleton'
import { DataState } from 'pejer/src/types/types'
import { computed } from 'vue'

const props = defineProps<{
  invoiceDetails: InvoiceDetails | undefined
  dataState: DataState
}>()

const companyInfo = computed(() => props.invoiceDetails?.sellerDetails)

function printInvoice() {
  window.print()
}

const isCredited = computed(() => {
  return (
    props.invoiceDetails?.paymentStatus === 'CancelledPayment' ||
    props.invoiceDetails?.paymentStatus === 'RefundedPayment' ||
    props.invoiceDetails?.paymentStatus === 'RefundInitiated'
  )
})
</script>
<template>
  <div class="flex flex-col-reverse justify-between sm:flex-row gap-4">
    <div class="w-full">
      <DataStateSkeleton :dataState="dataState" class="w-64 h-7 mb-2">
        <h1 class="text-2xl font-semibold">{{ companyInfo?.name }}</h1>
      </DataStateSkeleton>
      <HorizontalLine class="border-slate-200 my-1" />
      <p class="flex gap-2">
        <DataStateSkeleton :dataState="dataState" class="w-72 h-6">
          {{ companyInfo?.address }}, {{ companyInfo?.postalCode }} {{ companyInfo?.city }}
        </DataStateSkeleton>
      </p>
      <p class="flex gap-2">
        <span class="font-medium">Org.nr. </span>
        <DataStateSkeleton :dataState="dataState" class="self-center w-24">{{
          companyInfo?.organizationNumber
        }}</DataStateSkeleton>
      </p>
      <!-- <p class="flex gap-2">
          <span class="font-medium">IBAN:</span>
          <DataStateSkeleton :dataState="dataState" class="self-center w-24">{{
            companyInfo?.iban
          }}</DataStateSkeleton>
          |
          <span class="font-medium">BIC: </span>
          <DataStateSkeleton :dataState="dataState" class="self-center w-24">{{
            companyInfo?.bic
          }}</DataStateSkeleton>
        </p> -->
      <!-- <p class="flex gap-2">
              <span class="font-medium">Telefon: </span>
              <DataStateSkeleton :dataState="dataState" class="self-center w-24">{{
                companyInfo?.phoneNumber
              }}</DataStateSkeleton>
            </p> -->
    </div>
    <div class="flex flex-col min-h-full min-w-1/3 max-w-96 gap-2">
      <div
        class="text-center text-2xl bg-slate-50 p-4 rounded-sm border print:border-2 border-slate-200 font-semibold"
      >
        <h1><slot /></h1>
      </div>
      <div class="bg-slate-50 p-4 rounded-sm border border-slate-200 print:hidden">
        <h2 class="font-semibold text-slate-700 text-md">Nåværende status</h2>
        <HorizontalLine class="border-slate-200" />
        <div class="mt-2">
          <div class="flex flex-col gap-2">
            <Skeleton
              v-if="dataState.isLoading.value || dataState.isError.value"
              class="w-16 h-5"
            />
            <Badge v-else-if="isCredited" variant="destructive" class="w-fit"> Kredittert </Badge>
            <Badge
              v-else-if="invoiceDetails?.invoice.paidAmount === invoiceDetails?.invoice.grossAmount"
              variant="success"
              class="w-fit"
            >
              Betalt
            </Badge>
            <Badge
              v-else-if="
                invoiceDetails?.invoice.paidAmount === 0 ||
                invoiceDetails?.invoice.paidAmount === null
              "
              variant="default"
              class="w-fit"
            >
              Ubetalt
            </Badge>
            <Badge v-else variant="warning" class="w-fit">
              Delvis betalt <br />
              {{ invoiceDetails?.invoice.paidAmount / 100 }} kr av
              {{ invoiceDetails?.invoice.grossAmount / 100 }} kr
            </Badge>
          </div>
        </div>
        <Button
          variant="link"
          class="flex gap-2 mt-auto items-center text-sm font-semibold text-gray-700"
          @click="printInvoice"
        >
          <Printer class="w-4 h-4" /> Skriv ut faktura
        </Button>
      </div>
    </div>
  </div>
</template>
